import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { fmt$, fmt$0 } from '../utils/formatters';
import { updateVendor, resolveVendor, findSimilarVendors, assignVendorAlias, loadAllVendorsForUI } from '../lib/vendors';
import InvoiceOriginalViewer from './InvoiceOriginalViewer';
import { sharedConfirmInvoice } from '../lib/confirmInvoice';

export default function ExpenseInvoiceReviewPage({taskId,stagingId:propStagingId,orgId,uid,settings,setOtherExp,t,si,sb,sh,sd,mn,setPg,refreshBell,onDone}){
const[staging,setStaging]=useState(null);
const[task,setTask]=useState(null);
const[vendorRow,setVendorRow]=useState(null);
const[loading,setLoading]=useState(true);
const[error,setError]=useState('');
const[expCategory,setExpCategory]=useState('');
const[notes,setNotes]=useState('');
const[saveDefault,setSaveDefault]=useState(false);
const[vendorIsNew,setVendorIsNew]=useState(false);
const[similarVendors,setSimilarVendors]=useState([]);
const[showVendorPicker,setShowVendorPicker]=useState(false);
const[allVendors,setAllVendors]=useState([]);
const[vendorPickerSearch,setVendorPickerSearch]=useState('');
const[resolvedVendorName,setResolvedVendorName]=useState(null);

useEffect(()=>{
  if(!orgId)return;
  if(!taskId&&!propStagingId)return;
  (async()=>{
    let stId=propStagingId;
    if(taskId){
      const{data:tk}=await supabase.from('tasks').select('*').eq('id',taskId).single();
      if(!tk){setError('Task not found');setLoading(false);return;}
      setTask(tk);
      stId=tk.payload?.staging_id||tk.target_id;
    }
    const{data:st}=await supabase.from('invoice_staging').select('*').eq('id',stId).single();
    if(!st){setError('Invoice data not found');setLoading(false);return;}
    setStaging(st);
    if(st.vendor){
      const resolved=await resolveVendor(orgId,st.vendor);
      if(resolved){
        setVendorRow(resolved);
        if(resolved.default_expense_category)setExpCategory(resolved.default_expense_category);
        if(resolved.name.toLowerCase()!==st.vendor.trim().toLowerCase())setResolvedVendorName(resolved.name);
      }else{
        setVendorIsNew(true);
        findSimilarVendors(orgId,st.vendor).then(s=>setSimilarVendors(s)).catch(()=>{});
      }
    }
    setLoading(false);
  })();
},[taskId,propStagingId,orgId]);

async function assignToExistingVendor(v){
  try{
    await assignVendorAlias(orgId,v.id,staging.vendor);
    setResolvedVendorName(v.name);
    setVendorRow(v);
    if(v.default_expense_category)setExpCategory(v.default_expense_category);
    setVendorIsNew(false);setSimilarVendors([]);setShowVendorPicker(false);
    if(v.vendor_category==='inventory'){setPg('invoice-review');}
  }catch(e){console.error('[ExpenseInvoiceReviewPage] Alias error:',e);}
}

async function confirmAndSave(){
  if(!staging)return;
  if(vendorIsNew){setError('Classify the vendor above (New expense vendor or match) before saving.');return;}
  if(!expCategory){setError('Pick an expense category before confirming.');return;}
  const vendor=resolvedVendorName||staging.vendor;
  if(!vendor?.trim()){setError('Vendor is required.');return;}
  setError('');
  const extracted=staging.extracted_data;
  const matched=staging.matched_items||extracted.line_items||[];

  try{
    const result=await sharedConfirmInvoice({
      orgId,uid,extracted,matched,vendor,
      invNumber:staging.invoice_number,
      invDate:staging.invoice_date||new Date().toISOString().split('T')[0],
      grandTotal:staging.grand_total,
      confirmPath:'expense',
      storagePath:staging.storage_path,
      setOtherExp,refreshBell,
      expenseMode:true,expenseCategory:expCategory,
      vendorCategory:'expense',
    });
    if(result?.error)throw new Error(result.error);
    const invoiceId=result.invoiceId;

    // Update vendor default category if requested
    if(saveDefault&&vendorRow){
      await updateVendor(vendorRow.id,{default_expense_category:expCategory});
    }

    // Snapshot + complete task (only if opened via task)
    if(task){
      const snapshot={vendor:extracted.vendor,invoice_number:extracted.invoice_number,invoice_date:extracted.invoice_date,grand_total:extracted.grand_total};
      const updatedPayload={...(task.payload||{}),invoice_snapshot:snapshot};
      const now=new Date().toISOString();
      await supabase.from('tasks').update({status:'completed',completed_at:now,completed_by:uid,payload:updatedPayload}).eq('id',task.id);
      await supabase.from('task_events').insert({org_id:orgId,task_id:task.id,event_type:'status_changed',old_value:'pending',new_value:'completed',actor_id:uid});
      await supabase.from('tasks').update({status:'completed',completed_at:now,completed_by:uid}).eq('org_id',orgId).eq('type','invoice_anomaly').eq('target_id',invoiceId).eq('status','pending');
    }

    // Delete staging
    await supabase.from('invoice_staging').delete().eq('id',staging.id);

    if(onDone)onDone();else setPg('tasks');
  }catch(e){
    console.error('[ExpenseInvoiceReviewPage] Confirm error:',e);
    setError(e.message||'Failed to save invoice');
  }
}

async function rejectInvoice(){
  if(!window.confirm('Reject and discard this invoice? The file and extracted data will be permanently deleted. This cannot be undone.'))return;
  try{
    if(staging.storage_path)await supabase.storage.from('invoices').remove([staging.storage_path]);
    await supabase.from('invoice_staging').delete().eq('id',staging.id);
    if(task){
      const extracted=staging.extracted_data;
      const snapshot={vendor:extracted?.vendor,invoice_number:extracted?.invoice_number,invoice_date:extracted?.invoice_date,grand_total:extracted?.grand_total};
      const updatedPayload={...(task.payload||{}),invoice_snapshot:snapshot};
      const now=new Date().toISOString();
      await supabase.from('tasks').update({status:'cancelled',completed_at:now,completed_by:uid,payload:updatedPayload}).eq('id',task.id);
      await supabase.from('task_events').insert({org_id:orgId,task_id:task.id,event_type:'status_changed',old_value:'pending',new_value:'cancelled',actor_id:uid});
    }
    if(refreshBell)refreshBell();
    if(onDone)onDone();else setPg('tasks');
  }catch(e){
    console.error('[ExpenseInvoiceReviewPage] Reject error:',e);
    setError(e.message||'Failed to reject invoice');
  }
}

if(loading)return <div style={{textAlign:'center',padding:40,color:t.td}}>Loading invoice data...</div>;
if(error&&!staging)return <div style={{textAlign:'center',padding:40}}><div style={{color:t.ac,marginBottom:12}}>{error}</div><button onClick={()=>{if(onDone)onDone();else setPg('tasks');}} style={{...sb,background:t.bl}}>Back</button></div>;

const extracted=staging.extracted_data;
const lineItems=(staging.matched_items||extracted.line_items||[]).filter(i=>i.amount&&i.amount>0);
const lineSum=lineItems.reduce((s,i)=>s+(i.amount||0),0);
const extras=(extracted.tax||0)+(extracted.freight||0)+(extracted.bottle_deposit||0);
const discount=Number(extracted.discount)||0;
const expectedTotal=lineSum+extras-discount;
const mathDiff=Math.abs(expectedTotal-(extracted.grand_total||0));
const hasMathIssue=mathDiff>0.50&&extracted.grand_total>0;
const expCats=(settings?.expenseCategories||[]).sort();
const defaultChanged=vendorRow?.default_expense_category&&expCategory!==vendorRow.default_expense_category;

return(<div>
{vendorIsNew&&<div style={{background:'rgba(212,168,36,0.08)',border:`2px solid ${t.yl}`,borderRadius:8,padding:'16px 20px',marginBottom:16}}>
<div style={{fontSize:14,fontWeight:700,marginBottom:4}}>New vendor: {staging.vendor}</div>
<div style={{fontSize:12,color:t.tm,marginBottom:12}}>First time we've seen this vendor. Match to an existing vendor or classify as new.</div>
{similarVendors.length>0&&<div style={{marginBottom:12}}>
<div style={{fontSize:11,fontWeight:600,color:t.td,marginBottom:6}}>Did you mean one of these?</div>
{similarVendors.map(v=><div key={v.id} onClick={()=>assignToExistingVendor(v)} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',border:`1px solid ${t.bd}`,borderRadius:6,marginBottom:4,cursor:'pointer',background:t.cd}} onMouseOver={e=>e.currentTarget.style.background=t.sfh||t.sf} onMouseOut={e=>e.currentTarget.style.background=t.cd}>
<span style={{fontSize:12,fontWeight:500}}>{v.name}</span>
<span style={{fontSize:10,color:t.td}}>{Math.round(v.score*100)}% match</span>
</div>)}
</div>}
<div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
<button onClick={()=>setVendorIsNew(false)} style={{...sb,background:t.ac,color:'#fff'}}>Save as New Vendor</button>
<button onClick={async()=>{if(!allVendors.length){const v=await loadAllVendorsForUI(orgId);setAllVendors(v);}setShowVendorPicker(true);}} style={{background:'none',border:'none',color:t.bl,cursor:'pointer',fontSize:11,fontFamily:"'DM Sans',sans-serif",padding:0}}>Match to a different vendor →</button>
</div>
{showVendorPicker&&<div style={{marginTop:12,border:`1px solid ${t.bd}`,borderRadius:8,background:t.cd,padding:12,maxHeight:240,overflow:'auto'}}>
<input value={vendorPickerSearch} onChange={e=>setVendorPickerSearch(e.target.value)} placeholder="Search vendors..." style={{...si,width:'100%',marginBottom:8,boxSizing:'border-box'}} autoFocus/>
{allVendors.filter(v=>!vendorPickerSearch||v.name.toLowerCase().includes(vendorPickerSearch.toLowerCase())).map(v=><div key={v.id} onClick={()=>assignToExistingVendor(v)} style={{padding:'6px 10px',cursor:'pointer',borderBottom:`1px solid ${t.bd}`,fontSize:12}} onMouseOver={e=>e.currentTarget.style.background=t.sfh||t.sf} onMouseOut={e=>e.currentTarget.style.background='transparent'}>{v.name}{v.vendor_category==='expense'&&<span style={{fontSize:9,color:t.td,marginLeft:6}}>Expense</span>}</div>)}
</div>}
</div>}
<div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
<button onClick={()=>{if(onDone)onDone();else setPg('tasks');}} style={{...sb,background:'transparent',color:t.tm,padding:'4px 0',fontSize:11}}>← Back</button>
<h1 style={{fontSize:20,fontWeight:700,margin:0}}>Review Expense Invoice</h1>
</div>
{staging.storage_path?<InvoiceOriginalViewer storagePath={staging.storage_path} t={t}/>:<div style={{border:`1px solid ${t.bd}`,borderRadius:8,background:t.sf,padding:'10px 16px',marginBottom:16,fontSize:11,color:t.td,fontStyle:'italic'}}>Preview unavailable — no PDF stored with this invoice</div>}

{/* Header cards */}
<div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:16}}>
{[["Vendor",extracted.vendor,t.bl],["Invoice #",extracted.invoice_number||"—",t.tm],["Date",extracted.invoice_date||"—",t.tm],["Total","$"+fmt$(extracted.grand_total),t.gn]].map(([l,v,c])=>
<div key={l} style={{background:t.sf,borderRadius:8,padding:"10px 12px"}}>
<div style={{fontSize:9,color:t.td,textTransform:"uppercase"}}>{l}</div>
<div style={{fontSize:14,fontWeight:600,color:c,marginTop:2}}>{v}</div>
</div>)}
</div>

{/* Math check */}
{hasMathIssue&&<div style={{background:"rgba(212,168,36,0.08)",border:`1px solid ${t.yl}`,borderRadius:8,padding:14,marginBottom:14}}>
<div style={{fontSize:13,fontWeight:600,color:t.yl}}>Line items ${fmt$0(lineSum)}{extras>0?` + charges ${fmt$0(extras)}`:''}{discount>0?` − discount ${fmt$0(discount)}`:''} = ${fmt$0(expectedTotal)}. Invoice says ${fmt$0(extracted.grand_total)}. ${fmt$0(mathDiff)} difference.</div>
</div>}

{/* Expense category selection */}
<div style={{background:t.sf,borderRadius:8,padding:'16px 20px',marginBottom:16,border:`1px solid ${t.bd}`}}>
<label style={{fontSize:12,fontWeight:600,marginBottom:8,display:'block'}}>Expense Category *</label>
<select value={expCategory} onChange={e=>{setExpCategory(e.target.value);setError('');}} style={{...si,width:'100%',maxWidth:320,fontSize:13,padding:'8px 10px'}}>
<option value="">Select category...</option>
{expCats.map(c=><option key={c} value={c}>{c}</option>)}
</select>
<div style={{fontSize:10,color:t.td,marginTop:6}}>This invoice will be logged under this category on the Other Expenses page.</div>
{defaultChanged&&<label style={{display:'flex',alignItems:'center',gap:6,marginTop:8,cursor:'pointer'}}>
<input type="checkbox" checked={saveDefault} onChange={e=>setSaveDefault(e.target.checked)}/>
<span style={{fontSize:10,color:t.tm}}>Save this as the new default for {staging.vendor}</span>
</label>}
{error&&error.includes('category')&&<div style={{fontSize:10,color:t.ac,marginTop:6}}>{error}</div>}
</div>

{/* Notes */}
<div style={{marginBottom:16}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',marginBottom:4,display:'block'}}>Notes</label>
<textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Optional — e.g., 'Boiler repair' or 'June service'" style={{...si,width:'100%',resize:'vertical',boxSizing:'border-box'}}/>
</div>

{/* Line items (display only) */}
<div style={{fontSize:10,color:t.td,marginBottom:6,fontStyle:'italic'}}>All line items are stored for monitoring but do not match to your price sheet.</div>
<div style={{overflow:"auto",marginBottom:14}}>
<table style={{width:"100%",borderCollapse:"collapse",minWidth:500}}>
<thead><tr style={{background:t.sf}}>
{["Item","Description","Brand","Qty","Unit","Price","Amount"].map(h=><th key={h} style={sh}>{h}</th>)}
</tr></thead>
<tbody>{lineItems.map((item,i)=>(
<tr key={i} style={{borderBottom:`1px solid ${t.bd}`}}>
<td style={{...sd,fontSize:11,color:t.td}}>{item.item_number||"—"}</td>
<td style={{...sd,fontSize:12,fontWeight:500}}>{item.description}</td>
<td style={{...sd,fontSize:11,color:t.tm}}>{item.brand||"—"}</td>
<td style={{...sd,fontFamily:mn,fontSize:12}}>{item.qty_shipped}</td>
<td style={{...sd,fontSize:10}}>{item.unit||"—"}</td>
<td style={{...sd,fontFamily:mn,fontSize:12}}>${fmt$(item.unit_price||0)}</td>
<td style={{...sd,fontFamily:mn,fontSize:12,fontWeight:500}}>${fmt$(item.amount||0)}</td>
</tr>))}</tbody>
</table></div>

{/* Totals */}
<div style={{display:"flex",justifyContent:"flex-end",gap:16,marginBottom:14}}>
<div style={{textAlign:"right"}}>
{extracted.subtotal&&<div style={{fontSize:11,color:t.td}}>Subtotal: <span style={{fontFamily:mn}}>${fmt$(extracted.subtotal)}</span></div>}
{extracted.tax>0&&<div style={{fontSize:11,color:t.td}}>Tax: <span style={{fontFamily:mn}}>${fmt$(extracted.tax)}</span></div>}
{extracted.freight>0&&<div style={{fontSize:11,color:t.td}}>Freight/Fuel: <span style={{fontFamily:mn}}>${fmt$(extracted.freight)}</span></div>}
{extracted.bottle_deposit>0&&<div style={{fontSize:11,color:t.td}}>Bottle Deposit: <span style={{fontFamily:mn}}>${fmt$(extracted.bottle_deposit)}</span></div>}
{discount>0&&<div style={{fontSize:11,color:t.gn}}>Discount: <span style={{fontFamily:mn}}>−${fmt$(discount)}</span></div>}
<div style={{fontSize:14,fontWeight:700,marginTop:4}}>Total: <span style={{fontFamily:mn,color:t.gn}}>${fmt$(extracted.grand_total)}</span></div>
</div>
</div>

{/* Actions */}
<div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
<button onClick={rejectInvoice} style={{...sb,background:'rgba(200,55,45,0.08)',color:t.ac,border:`1px solid ${t.ac}`}}>Reject</button>
<button onClick={()=>{if(onDone)onDone();else setPg('tasks');}} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`}}>Cancel</button>
<button onClick={confirmAndSave} disabled={vendorIsNew} style={{...sb,background:vendorIsNew?t.sf:t.gn,color:vendorIsNew?t.td:'#fff',padding:"10px 24px",opacity:vendorIsNew?0.6:1,cursor:vendorIsNew?'not-allowed':'pointer',border:vendorIsNew?`1px solid ${t.bd}`:'none'}}>{vendorIsNew?'Select a vendor above first':'Confirm & Save'}</button>
</div>
{vendorIsNew&&<div style={{fontSize:13,fontWeight:600,color:t.yl,marginTop:8,textAlign:'right'}}>Classify the vendor above before saving.</div>}
{error&&!error.includes('category')&&<div style={{fontSize:11,color:t.ac,marginTop:10}}>{error}</div>}
</div>);
}
